import * as React from "react";
import { MessageSquareQuote } from "lucide-react";
import { cn } from "@/lib/utils";

// Grouped by what they change; each one is a single change, the way refining works best.
const GROUPS: { label: string; examples: string[] }[] = [
  {
    label: "Text and type",
    examples: ["Make the quote bigger", "Use a serif font for the headline", "Make the player's name stand out more"],
  },
  {
    label: "Layout",
    examples: [
      "Move player disclaimer below their title",
      "Put the stars above the quote",
      "Add more space around the text",
      "Move the headshot to the left",
    ],
  },
  {
    label: "Color",
    examples: ["Use warmer colors", "Use one brand color throughout", "Make the card darker so the text pops"],
  },
  {
    label: "Over the photo",
    examples: [
      "Add a soft dark overlay behind the text",
      "Show more of the photo",
      "Frame the photo with a thin border",
    ],
  },
  {
    label: "Every slide",
    examples: ["Use warmer colors on every slide", "Make the text bigger everywhere", "Match the first slide's layout"],
  },
];

interface RefineExamplesProps {
  /** Fills the refine box; the examples are read-only without it (nothing can be sent right now). */
  onPick?(text: string): void;
}

export const RefineExamples: React.FC<RefineExamplesProps> = ({ onPick }) => (
  <div className="space-y-3">
    {GROUPS.map(group => (
      <div key={group.label} className="grid grid-cols-1 gap-1.5 md:grid-cols-[120px_1fr] md:items-baseline">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{group.label}</div>
        <ul className="flex flex-wrap gap-1.5">
          {group.examples.map(example => {
            const content = (
              <>
                <MessageSquareQuote className="size-3.5 shrink-0 text-primary" aria-hidden="true" />
                {example}
              </>
            );
            const chip =
              "inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-2.5 py-1 text-sm text-slate-700 shadow-xs";
            return (
              <li key={example}>
                {onPick ? (
                  <button
                    type="button"
                    onClick={() => onPick(example)}
                    className={cn(chip, "cursor-pointer transition-colors hover:border-primary hover:bg-primary/5")}
                  >
                    {content}
                  </button>
                ) : (
                  <span className={chip}>{content}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </div>
);
